import React, { useState } from "react";
import { Alert, Button, Col, Form, Row, Card } from "react-bootstrap";



export const API_URL = "aws.api.url";
export const imagePath = "insurance/images/PET-Images/";

export const heroImages = [
  { src: "AdobeStock_74093746_DOG-Preview.jpeg", alt: "Happy dogs at the park" },
  { src: "AdobeStock_1320490477_CAT-Preview.jpeg", alt: "Travel Cat" },
  { src: "AdobeStock_118001293_DOG-Preview-FLIP.jpg", alt: "Puppy and owner" },
  { src: "AdobeStock_207653094_DOG-Bandage-Preview-FLIP.jpg", alt: "Doggy at the Vet - bandage" },
  { src: "AdobeStock_769356417_CAT-Bandage-Preview-FLIP.jpg", alt: "Cat at the Vet - bandage" },
  { src: "AdobeStock_1596964140_CAT-COOL-Preview-FLIP.jpg", alt: "Cool CAT" },
];



interface IQuote_DATA_Payload {
  firstName: string;
  lastName: string;
  petType: "dog" | "cat";
  petName?: string;
  breed?: string;
  age?: string;
  zip: string;
  email: string;
  phone: string;
  notes?: string;
  consent: boolean;
  terms: boolean;
}

interface QuickQuoteFormProps {
  clientVersion: string;
  testMode: boolean;
  apiUrl: string;
  themeCardShadow: string;
}

const isValidEmail = (v: string) => /.+@.+\..+/.test(v);
const digitsOnly = (v: string) => v.replace(/\D+/g, "");

export const PET_Insurance_QuoteForm_v01: React.FC<QuickQuoteFormProps> = ({
  clientVersion,
  testMode,
  apiUrl,
  themeCardShadow,
}) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [petType, setPetType] = useState<IQuote_DATA_Payload["petType"]>("dog");
  const [petName, setPetName] = useState("");
  const [breed, setBreed] = useState("");
  const [age, setAge] = useState("");
  const [zip, setZip] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [consent, setConsent] = useState(true);
  const [terms, setTerms] = useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const resetAlerts = () => {
    setError(null);
    setSuccess(null);
  };

  const validate = (): string | null => {
    if (!firstName) return "Please enter your first name.";
    if (!lastName) return "Please enter your last name.";
    if (!zip || digitsOnly(zip).length < 5) return "Please enter a valid 5-digit ZIP code.";
    if (!email || !isValidEmail(email)) return "Please enter a valid email address.";
    if (!phone || digitsOnly(phone).length < 10) return "Please enter a valid phone number.";
    if (!terms) return "Please accept the Terms to continue.";
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    resetAlerts();

    const validation = validate();
    if (validation) {
      setError(validation);
      return;
    }

    const payload_DATA: IQuote_DATA_Payload = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      petType,
      petName: petName.trim() || undefined,
      breed: breed.trim() || undefined,
      age: age.trim() || undefined,
      zip: digitsOnly(zip).slice(0, 5),
      email: email.trim(),
      phone: digitsOnly(phone),
      notes: notes.trim() || undefined,
      consent,
      terms,
    };

    try {
      setBusy(true);
      if (testMode) {
        console.log(payload_DATA);
        await new Promise((r) => setTimeout(r, 700));
        setSuccess("Thanks! We'll email your quote options shortly.");
      } else {
        const res = await fetch(apiUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload_DATA),
        });
        if (!res.ok) throw new Error(`Request failed: ${res.status}`);
        setSuccess("Thanks! We'll email your quote options shortly.");
      }
    } catch (err: any) {
      console.error(err);
      setError(err?.message || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card style={{ boxShadow: themeCardShadow, borderRadius: 14 }}>
      <Card.Body>
        <div className="d-flex align-items-center justify-content-between mb-2">
          <h5 className="m-0">Quick Quote</h5>
          <small className="text-muted">
            {clientVersion}
            {testMode ? " · Test" : ""}
          </small>
        </div>
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}
        {success && <Alert variant="success" className="py-2">{success}</Alert>}

        <Form onSubmit={handleSubmit}>
          <Row className="g-3">
            <Col md={6}>
              <Form.Label>First Name</Form.Label>
              <Form.Control value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </Col>
            <Col md={6}>
              <Form.Label>Last Name</Form.Label>
              <Form.Control value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </Col>

            <Col md={6}>
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={email}
                placeholder="you@example.com"
                onChange={(e) => setEmail(e.target.value)}
              />
            </Col>
            <Col md={6}>
              <Form.Label>Phone (required)</Form.Label>
              <Form.Control
                inputMode="tel"
                placeholder="(555) 555-5555"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
              />
            </Col>

            <Col xs={12}>
              <Form.Label>Pet Type</Form.Label>
              <div className="d-flex gap-3">
                <Form.Check
                  type="radio"
                  id="pet-dog"
                  name="petType"
                  label="Dog"
                  checked={petType === "dog"}
                  onChange={() => setPetType("dog")}
                />
                <Form.Check
                  type="radio"
                  id="pet-cat"
                  name="petType"
                  label="Cat"
                  checked={petType === "cat"}
                  onChange={() => setPetType("cat")}
                />
              </div>
            </Col>

            <Col md={6}>
              <Form.Label>Pet Name (optional)</Form.Label>
              <Form.Control value={petName} onChange={(e) => setPetName(e.target.value)} />
            </Col>
            <Col md={6}>
              <Form.Label>Breed (optional)</Form.Label>
              <Form.Control value={breed} onChange={(e) => setBreed(e.target.value)} />
            </Col>

            <Col md={6}>
              <Form.Label>Age (optional)</Form.Label>
              <Form.Select value={age} onChange={(e) => setAge(e.target.value)}>
                <option value="">Select age</option>
                <option value="0-1">0–1 years</option>
                <option value="2-5">2–5 years</option>
                <option value="6-8">6–8 years</option>
                <option value="9+">9+ years</option>
              </Form.Select>
            </Col>
            <Col md={6}>
              <Form.Label>ZIP Code</Form.Label>
              <Form.Control value={zip} onChange={(e) => setZip(e.target.value)} />
            </Col>

            <Col xs={12}>
              <Form.Label>Notes (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Col>

            <Col xs={12}>
              <Form.Check
                id="consent"
                type="checkbox"
                label="I agree to be contacted about my quote (email/SMS)."
                checked={consent}
                onChange={(e) => setConsent(e.currentTarget.checked)}
              />
              <Form.Check
                id="terms"
                type="checkbox"
                label={<span>I accept the <a href="#">Terms</a> & <a href="#">Privacy</a>.</span>}
                checked={terms}
                onChange={(e) => setTerms(e.currentTarget.checked)}
              />
            </Col>

            <Col xs={12} className="d-grid">
              <Button type="submit" size="lg" variant="success" disabled={busy}>
                {busy ? "Submitting…" : "See My Options"}
              </Button>
            </Col>
          </Row>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default PET_Insurance_QuoteForm_v01;
